import { MutationTree } from "vuex/types";
import Vue from "vue";
import { BroadcastState, IBroadcast } from "./types";
import _ from "lodash";
import moment from "moment";

/**
 * Mutations for the Broadcast module.
 * Handles synchronous state updates for broadcast management.
 */
const mutations: MutationTree<BroadcastState> = {
  /**
   * Sets multiple broadcasts into the state.
   *
   * @param {BroadcastState} state - Module state
   * @param {Object} payload - Mutation payload
   * @param {Array<IBroadcast>} payload.broadcasts - Array of broadcast objects
   */
  SET_BROADCASTS(state, { broadcasts }) {
    broadcasts.forEach((broadcast: IBroadcast) => {
      Vue.set(state.broadcastObjects, broadcast.timestamp, broadcast);
    });
  },

  /**
   * Sets a single broadcast, computing timestamp from date.
   *
   * @param {BroadcastState} state - Module state
   * @param {Object} payload - Mutation payload
   * @param {IBroadcast} payload.broadcast - Broadcast object
   */
  SET_BROADCAST(state, { broadcast }) {
    const newTimestamp = moment(broadcast.date).format("x");

    Vue.set(state.broadcastObjects, newTimestamp, broadcast);
  },

  /**
   * Removes a broadcast by timestamp.
   *
   * @param {BroadcastState} state - Module state
   * @param {string} timestamp - Timestamp key to remove
   */
  UNSET_BROADCAST(state, timestamp) {
    Vue.delete(state.broadcastObjects, timestamp);
  },

  /**
   * Sets the currently selected broadcast.
   *
   * @param {BroadcastState} state - Module state
   * @param {string} broadcastTimestamp - Timestamp of broadcast to select
   */
  SET_SELECTED_BROADCAST(state, broadcastTimestamp) {
    Vue.set(state, "selectedBroadcastId", broadcastTimestamp);

    const selectedBroadcast = _.get(state.broadcastObjects, `[${broadcastTimestamp}]`, null);
    Vue.set(state, "selectedBroadcast", selectedBroadcast);
  },

  /**
   * Clears the selected broadcast.
   *
   * @param {BroadcastState} state - Module state
   */
  CLEAR_SELECTED_BROADCAST(state, _) {
    Vue.set(state, "selectedBroadcastId", "");
    Vue.set(state, "selectedBroadcast", null);
  },

  /**
   * Creates a new broadcast in state.
   *
   * @param {BroadcastState} state - Module state
   * @param {Object} payload - Mutation payload
   * @param {IBroadcast} payload.broadcast - Broadcast object to create
   * @param {string} payload.timestamp - Timestamp key
   */
  CREATE_BROADCAST(state, { broadcast, timestamp }) {
    Vue.set(state.broadcastObjects, timestamp, broadcast);
  },

  /**
   * Deletes a broadcast and clears selection if it was selected.
   *
   * @param {BroadcastState} state - Module state
   * @param {string} timestamp - Timestamp of broadcast to delete
   */
  DELETE_BROADCAST(state, timestamp) {
    Vue.delete(state, "selectedBroadcastId");
    Vue.delete(state, "selectedBroadcast");
    Vue.delete(state.broadcastObjects, timestamp);
  },

  /**
   * Updates an existing broadcast.
   *
   * @param {BroadcastState} state - Module state
   * @param {Object} payload - Mutation payload
   * @param {string} payload.timestamp - Timestamp key
   * @param {IBroadcast} payload.broadcast - Updated broadcast object
   */
  UPDATE_BROADCAST(state, { timestamp, broadcast }) {
    console.log({ timestamp, broadcast });
    Vue.set(state.broadcastObjects, timestamp, broadcast);
  },
};

export default mutations;
